import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type { TimestampFormat } from "@cmmd-forge/contracts/settings";

import { cn } from "../../lib/utils";
import type { RightPanelKind } from "../../rightPanelSurface";
import { SidebarInset } from "../ui/sidebar";
import type { CloudRuntimeRouteBlockState } from "../../routes/ensureActiveRouteCloudRuntime";
import { resolveCloudThreadRoutePresentation } from "../../routes/-chatThreadRoute.logic";
import { RightPanelInlineSidebar, RightPanelSheet } from "../ThreadRightPanelRouteLayout";
import { BrowserMiniPlayerLayer } from "../../browser/miniPlayer/BrowserMiniPlayerLayer";
import ChatView from "../ChatView";
import { BreadcrumbThreadActions } from "../breadcrumb/BreadcrumbThreadActions";
import type { RightSidebarShellProps } from "../RightSidebarShell";
import { ChatThreadRightSidebar } from "./ChatThreadRightSidebar";

interface ChatThreadSurfaceProps {
  readonly cloudRouteBlockState: CloudRuntimeRouteBlockState | null;
  readonly markDiffOpened: () => void;
  readonly rightSidebarOpen: boolean;
  readonly rightPanelMaximized: boolean;
  readonly threadRef: ScopedThreadRef;
}

function ChatThreadSurface(props: ChatThreadSurfaceProps) {
  const presentation = resolveCloudThreadRoutePresentation(props.cloudRouteBlockState);
  return (
    <SidebarInset
      data-chat-column-maximized-away={props.rightPanelMaximized ? "true" : "false"}
      className={cn(
        "h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground",
        props.rightPanelMaximized && "hidden",
      )}
    >
      <BreadcrumbThreadActions threadRef={props.threadRef} />
      {presentation.showHistory ? (
        <ChatView
          environmentId={props.threadRef.environmentId}
          threadId={props.threadRef.threadId}
          onDiffPanelOpen={props.markDiffOpened}
          showRightPanelLauncher={!props.rightSidebarOpen}
          routeKind="server"
        />
      ) : null}
      <BrowserMiniPlayerLayer threadRef={props.threadRef} />
    </SidebarInset>
  );
}

export interface ChatThreadRouteLayoutProps {
  readonly cloudRouteBlockState: CloudRuntimeRouteBlockState | null;
  readonly closeRightPanel: () => void;
  readonly closeRightPanelSurfaceTab: (surfaceId: string) => void;
  readonly activateRightPanelSurface: (surfaceId: string) => void;
  readonly resourceLabels: RightSidebarShellProps["resourceLabels"];
  readonly markDiffOpened: () => void;
  readonly surfaces: RightSidebarShellProps["surfaces"];
  readonly activeSurfaceId: string | null;
  readonly previewTabId: string | undefined;
  readonly rightPanel: RightPanelKind | null;
  readonly rightSidebarOpen: boolean;
  readonly rightPanelMaximized: boolean;
  readonly onToggleRightPanelMaximized: () => void;
  readonly selectRightPanel: (kind: RightPanelKind) => void;
  readonly shouldRenderDiffContent: boolean;
  readonly shouldRenderRightPanel: boolean;
  readonly shouldUseRightPanelFullscreen: boolean;
  readonly shouldUseRightPanelSheet: boolean;
  readonly threadRef: ScopedThreadRef;
  readonly timestampFormat: TimestampFormat;
}

function RightPanelContent(
  props: ChatThreadRouteLayoutProps & { readonly mode: "sidebar" | "sheet" },
) {
  if (!props.shouldRenderRightPanel) return null;
  return (
    <ChatThreadRightSidebar
      closeRightPanel={props.closeRightPanel}
      closeRightPanelSurfaceTab={props.closeRightPanelSurfaceTab}
      resourceLabels={props.resourceLabels}
      rightPanelMaximized={props.rightPanelMaximized}
      onToggleRightPanelMaximized={props.onToggleRightPanelMaximized}
      mode={props.mode}
      surfaces={props.surfaces}
      activeSurfaceId={props.activeSurfaceId}
      activateRightPanelSurface={props.activateRightPanelSurface}
      previewTabId={props.previewTabId}
      rightPanel={props.rightPanel}
      selectRightPanel={props.selectRightPanel}
      shouldRenderDiffContent={props.shouldRenderDiffContent}
      threadRef={props.threadRef}
      timestampFormat={props.timestampFormat}
    />
  );
}

export function ChatThreadRouteLayout(props: ChatThreadRouteLayoutProps) {
  const threadSurface = (
    <ChatThreadSurface
      cloudRouteBlockState={props.cloudRouteBlockState}
      markDiffOpened={props.markDiffOpened}
      rightSidebarOpen={props.rightSidebarOpen}
      rightPanelMaximized={props.rightPanelMaximized}
      threadRef={props.threadRef}
    />
  );

  if (!props.shouldUseRightPanelSheet) {
    return (
      <>
        {threadSurface}
        <RightPanelInlineSidebar
          open={props.rightSidebarOpen}
          maximized={props.rightPanelMaximized}
          onClose={props.closeRightPanel}
        >
          <RightPanelContent {...props} mode="sidebar" />
        </RightPanelInlineSidebar>
      </>
    );
  }

  return (
    <>
      {threadSurface}
      {props.shouldRenderRightPanel ? (
        <RightPanelSheet
          open={props.rightSidebarOpen}
          fullscreen={props.shouldUseRightPanelFullscreen}
          onClose={props.closeRightPanel}
        >
          <RightPanelContent {...props} mode="sheet" />
        </RightPanelSheet>
      ) : null}
    </>
  );
}
